/**
 * As respostas do bot a meio, por conversa (ADR-0017, revisões #24 e #25).
 *
 * O texto chega em lotes (`bot.delta`), cada um com `offset` — onde começa. Um lote repetido (retoma, ou dois processos)
 * não duplica texto; um lote que salta texto (perdeu-se um pelo caminho) marca a execução para retomar por REST
 * (`GET /bot-runs/{id}`, o texto até ao último ponto de retoma). Quando acaba (`bot.done`), a resposta a sério chega
 * como mensagem normal e a execução sai daqui.
 */

export type BotRunStatus = "a_correr" | "feita" | "cancelada" | "falhou";

export interface LiveBotRun {
  id: string;
  conversationId: string;
  threadRootId: string | null;
  invokerId: string | null;
  text: string;
  /** O que o bot está a fazer agora («a pensar», a ferramenta, a fonte). */
  step: string | null;
  status: BotRunStatus;
  /** Faltou um lote: o texto tem de se repor por REST antes de continuar. */
  needsResume: boolean;
}

/** As execuções a meio de uma conversa, por id. */
export type LiveBotRuns = Record<string, LiveBotRun>;

interface RunRef {
  run_id: string;
  conversation_id: string;
  thread_root_id?: string | null;
  invoker_id?: string | null;
}

function base(runs: LiveBotRuns, ref: RunRef): LiveBotRun {
  return (
    runs[ref.run_id] ?? {
      id: ref.run_id,
      conversationId: ref.conversation_id,
      threadRootId: ref.thread_root_id ?? null,
      invokerId: ref.invoker_id ?? null,
      text: "",
      step: null,
      status: "a_correr",
      needsResume: false,
    }
  );
}

const STEP_LABEL: Record<string, string> = { fonte: "a consultar fontes…" };

export function applyStep(runs: LiveBotRuns, ref: RunRef & { kind: string; step: string }): LiveBotRuns {
  const run = base(runs, ref);
  // «Pensar» não diz nada de concreto: fica sem passo e o web mostra as frases de espera, que avançam com o tempo. Só uma
  // ferramenta ou uma fonte (algo que se pode nomear) vira texto.
  const step = ref.kind === "ferramenta" ? `a consultar ${ref.step.replaceAll("_", " ")}…` : ref.kind === "fonte" ? ref.step || STEP_LABEL.fonte : null;
  return { ...runs, [ref.run_id]: { ...run, step } };
}

export function applyDelta(runs: LiveBotRuns, ref: RunRef & { offset: number; delta: string }): LiveBotRuns {
  const run = base(runs, ref);
  if (run.needsResume) return runs;
  if (ref.offset > run.text.length) return { ...runs, [ref.run_id]: { ...run, needsResume: true } };
  // Um lote repetido ou sobreposto substitui a parte que já havia a partir do seu início: nunca duplica.
  const text = run.text.slice(0, ref.offset) + ref.delta;
  return { ...runs, [ref.run_id]: { ...run, text: text.length >= run.text.length ? text : run.text, step: null } };
}

export function applyResume(runs: LiveBotRuns, runId: string, partialText: string | null | undefined): LiveBotRuns {
  const run = runs[runId];
  if (!run) return runs;
  const text = partialText ?? "";
  return { ...runs, [runId]: { ...run, text: text.length >= run.text.length ? text : run.text, needsResume: false } };
}

/** Acabou: sai da lista (a resposta final chega como mensagem; cancelada ou falhada, o servidor publica o aviso). */
export function applyDone(runs: LiveBotRuns, runId: string): LiveBotRuns {
  if (!(runId in runs)) return runs;
  const next = { ...runs };
  delete next[runId];
  return next;
}

/** As execuções de uma janela (a conversa principal ou uma thread). */
export function runsFor(runs: LiveBotRuns | undefined, threadRootId: string | null): LiveBotRun[] {
  return Object.values(runs ?? {}).filter((r) => r.threadRootId === threadRootId);
}
